import React, { useRef, useState } from 'react';
import { Form } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from '@monorepo/hooks';
import { CAPTCHA_SITE_KEY, isCaptchaRequired } from '../../../core/config/captcha';
import styles from './index.module.scss';

// Tách riêng để nhận value/onChange do Form.Item tiêm xuống: bọc thẳng <div> thì antd truyền prop
// `value` vào DOM và React cảnh báo. onChange ghi token vào form, onVerify cho side-effect của form cha.
const CaptchaWidget = ({ onChange, onVerify, compact, captchaRef, onNotice }) => {
  const handleChange = (token) => {
    onNotice(null);
    onChange?.(token);
    onVerify?.(token);
  };

  // Google vẽ thông báo hết hạn/lỗi BÊN TRONG iframe nên không style hay dời được. reset() xoá nó
  // đi và trả widget về ô chưa tick; thông báo tương ứng hiện ở dưới bằng markup của mình.
  // Phải hoãn 1 tick: reset() gọi đồng bộ ngay trong expired-callback bị grecaptcha bỏ qua vì
  // widget đang chuyển trạng thái, dẫn tới dòng đỏ của Google nằm lại cùng dòng của mình.
  const clearWidget = (notice) => {
    setTimeout(() => captchaRef.current?.reset(), 0);
    onChange?.(null);
    onNotice(notice);
  };

  // Lỗi thì KHÔNG reset: domain chưa khai với Google hoặc mạng chặn Google thì tải lại vẫn lỗi,
  // reset sẽ thành vòng lặp lỗi → reset → lỗi làm widget nháy liên tục.
  const handleErrored = () => {
    onChange?.(null);
    onNotice('captchaErrored');
  };

  return (
    <div className={styles['inner']}>
      <ReCAPTCHA
        ref={captchaRef}
        sitekey={CAPTCHA_SITE_KEY}
        // Không đặt thì Google theo ngôn ngữ trình duyệt, mỗi máy một kiểu. hl truyền qua
        // grecaptcha.render (đã kiểm chứng: đè được cả hl trên URL script).
        hl="en"
        size={compact ? 'compact' : 'normal'}
        onChange={handleChange}
        onExpired={() => clearWidget('captchaExpired')}
        onErrored={handleErrored}
      />
    </div>
  );
};

const LookupCaptcha = ({ name = 'captcha', rules, onVerify }) => {
  const { t } = useTranslation();
  const { isFoldedPhone } = useWindowSize();
  const captchaRef = useRef();
  const [notice, setNotice] = useState(null);

  // Mặc định bắt buộc xác thực. Để ở đây thay vì từng form tự truyền, tránh chuyện một form quên.
  // Môi trường không bắt tick (dev/stg) thì vẫn hiện widget nhưng bỏ rule.
  const fieldRules = isCaptchaRequired
    ? (rules ?? [{ required: true, message: t('common.validateBlankReCaptcha') }])
    : [];

  // if/else qua help/validateStatus của chính Form.Item, KHÔNG dùng CSS display:none để ẩn chồng —
  // antd bỏ qua hoàn toàn lỗi rule-validate khi `help` được truyền (xem FormItem.js: mergedErrors =
  // help ?? meta.errors), nên chỉ 1 dòng tồn tại trong DOM, không có dòng nào bị ẩn mà vẫn chiếm chỗ.
  // Hết hạn thì token bị xoá khỏi Form nên rule required cũng fail — không có help thì antd sẽ tự
  // hiện "Vui lòng xác thực reCAPTCHA!" chồng lên thông báo hết hạn của mình.
  // Không bắt tick thì cũng không hiện dòng báo lỗi đỏ, tránh QC tưởng là lỗi cần báo.
  const helpProps = notice && isCaptchaRequired ? { validateStatus: 'error', help: t(`common.${notice}`) } : {};

  return (
    <div className={styles['container']}>
      <Form.Item name={name} rules={fieldRules} {...helpProps}>
        <CaptchaWidget compact={isFoldedPhone} onVerify={onVerify} captchaRef={captchaRef} onNotice={setNotice} />
      </Form.Item>
    </div>
  );
};

export default LookupCaptcha;
